/**
 * 避難場所マスターデータのロード・キャッシュサービス。
 *
 * - public/data/evacuation-places.tokyo.json をfetchで取得
 * - 初回ロード時にメモリキャッシュ
 * - 取得失敗時はエラーをthrow (UIで表示)
 *
 * 将来Firebase Firestore統合時は、本サービスをFirestore版に差し替えるだけで
 * 上流の useEvacuationSearch / PropertyNew は無変更で動く設計。
 */
import type {
  EvacuationCategory,
  DisasterType,
  EvacuationPlace,
} from '@/types/evacuation';

/**
 * バンドル/オープンデータJSONのスキーマ。
 * "places" 配列の各要素を EvacuationPlace に変換する。
 */
interface RawDataset {
  source?: string;
  license?: string;
  importedAt?: string;
  places: RawPlace[];
}

export interface RawPlace {
  id: string;
  name: string;
  category: string;
  categories?: string[];
  disasterTypes: string[];
  prefecture: string;
  municipality: string;
  point?: { lng: number; lat: number };
  /**
   * coordinates = 塗りエリア(外周ring)の配列。複数エリア対応 (広域避難場所の分割エリア)。
   * holes = 範囲の中の除外区域 (都の広域避難場所SHPの内側ring)。
   */
  polygon?: {
    coordinates: Array<Array<[number, number]>>;
    holes?: Array<Array<[number, number]>>;
  };
  /** OSM Overpass APIで polygon を引くときに使う構成施設名 */
  landmarkNames?: string[];
  /** polygon が実形状ではなく簡易円であることを示す (EvacuationPlace.approxShape 参照)。 */
  approxShape?: boolean;
  officialIds?: string[];
  source?: string;
  /** 受入対象者 (国土地理院 指定避難所データの列)。指定福祉避難所のみ記入。 */
  shelterFor?: string;
}

/** EvacuationPlace に landmarkNames を含める拡張型 (アプリ内のみ) */
export interface EvacuationPlaceExtended extends EvacuationPlace {
  landmarkNames?: string[];
}

// 原則「公式オープンデータ」をマージした東京都の避難場所マスター:
//  1. 東京都総務局「東京都防災マップ 避難所一覧」(指定避難所 約2560点) — 学校等の避難所
//  2. 東京都総務局「東京都防災マップ 避難場所一覧」(指定緊急避難場所 約2251点・災害種別付き)
//  3. 東京都都市整備局「震災時火災における避難場所」(広域避難場所 221件・ポリゴン) —
//     「駒場東大一帯」等の公式エリア形状 (複数エリア対応)。範囲塗りをそのまま描ける。
// 公式避難場所マスター (GRAND 展開エリア):
//  - tokyo: 都オープンデータ統合 (scripts/merge-evacuation-data.ts)。指定避難所/指定緊急/
//           広域避難場所を統合、全件ポリゴン焼き込み済み。
//  - osaka: 国土数値情報 指定緊急避難場所 (府全域)。approx円 or OSM敷地形状。
//  - kanagawa / それ以外 9 都市圏: 国土地理院 指定緊急避難場所データ (scripts/import-gsi-hinan.mjs)。
//           神奈川=県全域、他=都市圏抽出。形状は approx円、選択時に OSM でアップグレード。
//           出典: 国土地理院ウェブサイト (指定緊急避難場所データ) / 公共データ利用規約(PDL)1.0。
export const DATASET_URLS = [
  '/data/evacuation-places.tokyo.json',
  '/data/evacuation-places.kanagawa.json',
  // 横浜市の広域避難場所は市の独自指定で、国土地理院の指定緊急避難場所データ
  // (kanagawa) に入っていない。入れないと避難場所の自動選定が小学校を拾う
  // (納品済みの横浜3件で実際に起きた)。収録は表3.12 (2025-04-01) の全 112 か所 (全 18 区)。
  '/data/evacuation-places.koiki-yokohama.json',
  // 相模原市の広域避難場所 59 か所のうち国土地理院に無い 16 か所 (大学・ゴルフ場・大きな公園など。
  // 残る 43 か所は kanagawa の施設に区分を付けた)。無いと近くの学校が避難場所に選ばれる。
  '/data/evacuation-places.koiki-sagamihara.json',
  // 大阪市の広域避難場所 34 か所 (公式の範囲つき)。国土地理院には 16 か所しか無い。
  '/data/evacuation-places.koiki-osaka-city.json',
  '/data/evacuation-places.chiba.json',
  '/data/evacuation-places.saitama.json',
  '/data/evacuation-places.nagoya.json',
  '/data/evacuation-places.osaka.json',
  '/data/evacuation-places.kyoto.json',
  '/data/evacuation-places.kobe.json',
  '/data/evacuation-places.hiroshima.json',
  '/data/evacuation-places.fukuoka.json',
  '/data/evacuation-places.sendai.json',
  '/data/evacuation-places.sapporo.json',
];

let cachedPlaces: EvacuationPlaceExtended[] | null = null;
let inflightLoad: Promise<EvacuationPlaceExtended[]> | null = null;

/**
 * 避難場所マスターデータをロードする。複数回呼ばれてもメモリキャッシュを返す。
 * 並行呼び出しは同じ Promise を共有してネットワーク重複を防ぐ。
 */
export async function loadEvacuationPlaces(): Promise<EvacuationPlaceExtended[]> {
  if (cachedPlaces) return cachedPlaces;
  if (inflightLoad) return inflightLoad;

  inflightLoad = (async () => {
    // 全データセットを並列取得 (直列だと 件数×RTT が積算する)。
    // 結合は DATASET_URLS の順序を保つ (同距離時の候補選択の安定性のため)。
    const datasets = await Promise.all(
      DATASET_URLS.map(async (url) => {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(
            `避難場所データの取得に失敗しました (${response.status}): ${url}`,
          );
        }
        return { url, json: (await response.json()) as RawDataset };
      }),
    );
    const all: EvacuationPlaceExtended[] = [];
    for (const { url, json } of datasets) {
      for (const raw of json.places) {
        all.push(normalizeRawPlace(raw, json.source ?? url));
      }
    }
    cachedPlaces = all;
    return all;
  })();

  try {
    return await inflightLoad;
  } finally {
    inflightLoad = null;
  }
}

/**
 * テスト・デバッグ用にキャッシュをクリアする。
 */
export function clearEvacuationDataCache(): void {
  cachedPlaces = null;
  inflightLoad = null;
}

const KNOWN_CATEGORIES: EvacuationCategory[] = [
  '広域避難場所',
  '指定緊急避難場所',
  '指定避難所',
];
const KNOWN_DISASTERS: DisasterType[] = [
  '大火災',
  '洪水',
  '崖崩れ・土石流',
  '高潮',
  '地震',
  '津波',
  '内水氾濫',
  '火山',
  'その他',
];

const copyRing = (ring: Array<[number, number]>): Array<[number, number]> =>
  ring.map(([lng, lat]) => [lng, lat] as [number, number]);

/**
 * バンドル/オープンデータJSONの1件をアプリの EvacuationPlace へ変換する (純関数)。
 * Vite/実行時環境に依存しない (import は型のみ) ので、tsx のスクリプトからも同じ変換を使える
 * (scripts/check-assignment-regression.ts が正規の loader と結果を揃えるために利用)。
 */
export function normalizeRawPlace(raw: RawPlace, sourceFallback: string): EvacuationPlaceExtended {
  const category: EvacuationCategory = (KNOWN_CATEGORIES as string[]).includes(
    raw.category,
  )
    ? (raw.category as EvacuationCategory)
    : '指定緊急避難場所';
  const disasterTypes: DisasterType[] = raw.disasterTypes
    .filter((d): d is DisasterType => (KNOWN_DISASTERS as string[]).includes(d))
    .slice();
  const categories = (raw.categories ?? [raw.category]).filter(
    (c): c is EvacuationCategory => (KNOWN_CATEGORIES as string[]).includes(c),
  );
  return {
    id: raw.id,
    name: raw.name,
    category,
    categories: categories.length > 0 ? categories : [category],
    disasterTypes,
    prefecture: raw.prefecture,
    municipality: raw.municipality,
    point: raw.point,
    polygon: raw.polygon
      ? {
          coordinates: raw.polygon.coordinates.map(copyRing),
          // 除外区域も必ず引き継ぐ。落とすと、アプリで選んだ避難先 (新規・選び直し) が
          // 範囲外の街区や水面まで塗り、経路もそこへ入る (holes を持つのは都の広域避難場所)。
          ...(raw.polygon.holes && raw.polygon.holes.length > 0
            ? { holes: raw.polygon.holes.map(copyRing) }
            : {}),
        }
      : undefined,
    // 円か実形状かは消費側の分岐材料なので、必ず引き継ぐ (落とすと円が実形状に化ける)。
    approxShape: raw.approxShape,
    ...(raw.officialIds && raw.officialIds.length > 0
      ? { officialIds: [...raw.officialIds] }
      : {}),
    source: raw.source ?? sourceFallback,
    updatedAt: new Date().toISOString(),
    landmarkNames: raw.landmarkNames,
    ...(raw.shelterFor ? { shelterFor: raw.shelterFor } : {}),
  };
}
